import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import {
  Box, Card, Button, Typography, useTheme, IconButton, Fade,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import DragAndDropArea from '../../components/DragAndDropArea/DragAndDropArea';
import SkipTraceHelpWizard from '../../components/SkipTraceHelpWizard';
import PageTitle from '../../components/PageTitle';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as ArrowLeftIcon } from '../../assets/icons/arrow-left.svg';
import { paymentApi } from '../../api/services/userApi';
import config from '../../config';
import { useAppSelector } from '../../store/hooks';
import track from '../../utils/googleAnalyticsEvents';
import { SkipTraceFileTypeENUM } from '../../utils/skipTraceFileHeaders';
import mappingFieldsExample from '../../assets/mapping-fields-example.gif';

const getGuidedPerRecordPrice = (
  skipTraceType: string,
  rowCost: number,
  llcCost: number,
) => {
  if (skipTraceType === SkipTraceFileTypeENUM.BUSSINESS) {
    return { price: llcCost, decimals: 3 };
  }

  if (
    skipTraceType === SkipTraceFileTypeENUM.BLACKLISTED ||
    skipTraceType === SkipTraceFileTypeENUM.DNC
  ) {
    return { price: config.blacklistPricePerRecord, decimals: 2 };
  }

  return { price: rowCost, decimals: 3 };
};

const ImportListPage = () => {
  const user = useAppSelector((state) => state.userSlice.user);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const userSubscription = user.subscription;
  const [isSingleSkipTrace, setisSingleSkipTrace] = useState(false);
  const [isHelpWizard, setIsHelpWizard] = useState(false);
  const [guidedSkipTraceType, setGuidedSkipTraceType] = useState<string | null>(
    null,
  );
  const [ownerAddOnsStep, setOwnerAddOnsStep] = useState<string | null>(null);
  const [isMappingOpen, setIsMappingOpen] = useState(false);
  const [isMappingExampleDismissed, setIsMappingExampleDismissed] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const sessionId = searchParams.get('session_id');
  const theme = useTheme();
  const isGuidedUpload = Boolean(guidedSkipTraceType);
  const showMappingExample =
    isGuidedUpload && isMappingOpen && !isMappingExampleDismissed;
  const isEmailFree = config.freeEmailSubscriptions.includes(
    user.subscription?.type.toLowerCase() || '',
  );
  const guidedPrice = guidedSkipTraceType
    ? getGuidedPerRecordPrice(
      guidedSkipTraceType,
      user.rowCost,
      user.llcCost,
    )
    : null;

  const sidePanelSx = {
    width: { xs: '100%', md: 280 },
    flexShrink: 0,
    border: 2,
    borderColor: 'primary.main',
    borderRadius: 3,
    p: 3,
    minHeight: 180,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    textAlign: 'center',
  } as const;

  const sidePanelEnterSx = {
    ...sidePanelSx,
    animation: 'ownerAddOnSideEnter 0.35s ease',
    '@keyframes ownerAddOnSideEnter': {
      from: { opacity: 0, transform: 'translateY(12px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
  } as const;

  useEffect(() => {
    if (sessionId) {
      track.subscriptionEvent();
    }
  }, [sessionId]);

  const onAddSingleSkipTrace = () => {
    setisSingleSkipTrace(true);
  };

  const onCancelSingleSkipTrace = () => {
    setisSingleSkipTrace(false);
  };

  const onOpenHelpWizard = () => {
    setIsHelpWizard(true);
  };

  const onCloseHelpWizard = () => {
    setIsHelpWizard(false);
  };

  const onHelpWizardComplete = (skipTraceType: string) => {
    setGuidedSkipTraceType(skipTraceType);
    setIsHelpWizard(false);
    setIsMappingExampleDismissed(false);
  };

  const onCancelGuidedUpload = () => {
    setGuidedSkipTraceType(null);
    setIsFileUploaded(false);
    setOwnerAddOnsStep(null);
    setIsMappingOpen(false);
    setIsMappingExampleDismissed(false);
  };

  const onSubscription = async () => {
    try {
      if (!config.subscriptionPlans.starter.id) return;
      const res = await paymentApi.checkoutTrialSubscription(
        config.subscriptionPlans.starter.id,
        location.pathname,
      );
      if (res.status === StatusCodes.OK) {
        const link = document.createElement('a');
        link.href = res.data.data?.session.url || '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box
      sx={{
        px: '30px',
        py: '25px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <Box>
        {isFileUploaded || isSingleSkipTrace || isHelpWizard || isGuidedUpload ? (
          <PageTitle title="Skip Trace" />
        ) : (
          <PageTitle
            title="Skip Trace"
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Button
              endIcon={<PlusIcon fill="white" />}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'capitalize',
              }}
              variant="contained"
              onClick={() => onAddSingleSkipTrace()}
            // disabled={!userSubscription?.id}
            >
              Add Single Trace
            </Button>
          </PageTitle>
        )}
      </Box>
      <Card sx={{ p: { xs: 2, sm: 5 }, borderRadius: 5, position: 'relative' }}>
        <Box
        // sx={!userSubscription?.id ? {
        //   filter: 'blur(15px)',
        //   pointerEvents: 'none',
        // } : {}}
        >
          {(isSingleSkipTrace || (isGuidedUpload && !isFileUploaded)) && (
            <Button
              startIcon={<ArrowLeftIcon fill={theme.palette.primary.main} />}
              sx={{
                px: 2,
                borderRadius: 2,
                textTransform: 'capitalize',
              }}
              onClick={() => {
                if (isSingleSkipTrace) {
                  onCancelSingleSkipTrace();
                  return;
                }
                onCancelGuidedUpload();
              }}
            // disabled={!userSubscription?.id}
            >
              Back
            </Button>
          )}
          {isHelpWizard ? (
            <SkipTraceHelpWizard
              onBack={onCloseHelpWizard}
              onComplete={onHelpWizardComplete}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                alignItems: 'stretch',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <DragAndDropArea
                  key={guidedSkipTraceType || 'standard-upload'}
                  isSingleSkipTrace={isSingleSkipTrace}
                  onFileUploadChange={setIsFileUploaded}
                  onOwnerAddOnsStepChange={setOwnerAddOnsStep}
                  onMappingOpenChange={setIsMappingOpen}
                  presetSkipTraceType={guidedSkipTraceType || undefined}
                  hideTypeSelector={isGuidedUpload}
                />
              </Box>
              {!isSingleSkipTrace && !isFileUploaded && !isGuidedUpload && (
                <Box
                  sx={{
                    width: { xs: '100%', md: 280 },
                    flexShrink: 0,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h6" fontWeight={600}>
                    Need Help?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Not sure how to format your file or which skiptrace type to
                    use? We can walk you through it.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={onOpenHelpWizard}
                    sx={{
                      px: 3,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'capitalize',
                    }}
                  >
                    Help me skiptrace
                  </Button>
                </Box>
              )}
              {isGuidedUpload && !isSingleSkipTrace && ownerAddOnsStep === 'email' && (
                <Box sx={sidePanelEnterSx}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email add-on
                  </Typography>
                  <Typography
                    variant="h6"
                    color="primary.main"
                    fontWeight={600}
                  >
                    {isEmailFree
                      ? 'Free'
                      : `$${config.emailPricePerRecord.toFixed(2)}/record`}
                  </Typography>
                </Box>
              )}
              {isGuidedUpload &&
                !isSingleSkipTrace &&
                ownerAddOnsStep === 'dnc' && (
                <Box sx={sidePanelEnterSx}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Disclosure
                  </Typography>
                  <Typography variant="body1" color="primary.main" fontWeight={600}>
                    Estimated 40-60% phone number removal
                  </Typography>
                </Box>
              )}
              {isGuidedUpload &&
                !isSingleSkipTrace &&
                !isFileUploaded &&
                !ownerAddOnsStep &&
                guidedPrice && (
                <Box sx={sidePanelSx}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Selected skiptrace type
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {guidedSkipTraceType}
                  </Typography>
                  <Typography
                    variant="h6"
                    color="primary.main"
                    fontWeight={600}
                  >
                    {`$${guidedPrice.price.toFixed(guidedPrice.decimals)}/record`}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
        {/* {!userSubscription?.id && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LockIcon color="primary" sx={{ fontSize: '120px' }} />
            <Box display="flex" alignItems="center" gap={1}>
              <Typography>
                You need an active subscription to access these features.
              </Typography>
              <Button onClick={onSubscription} variant="contained">
                Subscribe
              </Button>
            </Box>
            {
              !user.hasUsedTrial && (
                <Typography>
                  Subscribe to start a free trial of 7 days.
                </Typography>
              )
            }
          </Box>
        )} */}
      </Card>

      <Fade in={showMappingExample} unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            top: { xs: 16, md: 16 },
            right: { xs: 12, md: 12 },
            zIndex: (themeValue) => themeValue.zIndex.snackbar,
            width: { xs: 'min(360px, calc(100vw - 24px))', md: 450 },
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 6,
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              Example
            </Typography>
            <IconButton
              size="small"
              aria-label="Dismiss mapping example"
              onClick={() => setIsMappingExampleDismissed(true)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            component="img"
            src={mappingFieldsExample}
            alt="Example of mapping source fields to destination fields"
            sx={{
              width: '100%',
              height: 'auto',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              display: 'block',
            }}
          />
        </Box>
      </Fade>
    </Box>
  );
};

export default ImportListPage;
